import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Video, Lock, PlayCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const WorkshopRecordings = ({ phone }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [wmPos, setWmPos] = useState({ top: '12%', left: '8%' });
  const videoRef = useRef(null);

  const token = localStorage.getItem('subscriber_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/workshop-recordings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) { const d = await res.json(); setItems(d.recordings || []); }
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [token]);

  // Move the watermark periodically so it can't be cropped out / hidden.
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      const tops = ['10%', '30%', '55%', '75%', '40%'];
      const lefts = ['8%', '55%', '25%', '65%', '38%'];
      setWmPos({ top: tops[Math.floor(Math.random() * tops.length)], left: lefts[Math.floor(Math.random() * lefts.length)] });
    }, 3500);
    return () => clearInterval(id);
  }, [active]);

  const today = new Date().toLocaleDateString('en-IN');
  const wmText = `${phone || 'UCS'} · ${today}`;

  if (loading) {
    return (
      <Card className="bg-gray-900/50 border-golden/30">
        <CardContent className="p-6 text-gray-400 text-sm">Loading workshop recordings…</CardContent>
      </Card>
    );
  }

  if (items.length === 0) return null; // nothing to show yet

  return (
    <Card className="bg-gray-900/50 border-golden/30" data-testid="workshop-recordings-section">
      <CardHeader>
        <CardTitle className="text-golden flex items-center gap-2">
          <Video className="w-5 h-5" /> Workshop Recordings
        </CardTitle>
        <CardDescription className="text-gray-400 flex items-center gap-1">
          <Lock className="w-3.5 h-3.5" /> For your eyes only — downloading is disabled and each stream is watermarked with your number.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {active && (
          <div
            className="relative rounded-lg overflow-hidden bg-black select-none"
            onContextMenu={(e) => e.preventDefault()}
            data-testid="workshop-video-wrap"
          >
            <video
              ref={videoRef}
              key={active.id}
              src={`${API_URL}/api/workshop-recordings/${active.id}/stream?auth=${encodeURIComponent(token)}`}
              controls
              autoPlay
              controlsList="nodownload noremoteplayback noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              className="w-full max-h-[70vh] bg-black"
              data-testid="workshop-video-player"
            />
            {/* Moving watermark — traceable to this account, cannot be interacted with */}
            <div
              className="absolute text-white/70 text-sm md:text-base font-mono font-semibold pointer-events-none select-none"
              style={{ top: wmPos.top, left: wmPos.left, textShadow: '0 1px 4px rgba(0,0,0,.95)', transition: 'top .8s, left .8s' }}
              data-testid="workshop-video-watermark"
            >
              {wmText}
            </div>
            {/* Static corner watermark (always visible even if the moving one is cropped) */}
            <div
              className="absolute bottom-14 right-3 text-white/50 text-xs font-mono pointer-events-none select-none"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,.95)' }}
            >
              {wmText}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {items.map((r, i) => (
            <button
              key={r.id}
              onClick={() => setActive(r)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${active?.id === r.id ? 'bg-golden/20 border border-golden/40' : 'bg-gray-800/50 hover:bg-gray-800 border border-transparent'}`}
              data-testid={`workshop-recording-${i}`}
            >
              <PlayCircle className={`w-5 h-5 ${active?.id === r.id ? 'text-golden' : 'text-gray-400'}`} />
              <span className="text-white flex-1">{r.title}</span>
              {active?.id === r.id && <span className="text-xs text-golden">Now playing</span>}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkshopRecordings;
